import {useMemo} from 'react';
import {Navigate, Route, Routes} from 'react-router-dom';
import {SDK_MODE} from 'utils/embedded';
import {
  useUniversalLink,
  useInitGuestTaxes,
  usePath,
  useInitPropertyProtection,
  useSetPreviousPage,
} from 'hooks';
import {useReservation} from 'context/reservation';
import {useCustomTheme} from 'context/customTheme';
import {useSummary} from 'context/summary';
import {useCheckoutSettings} from 'pages/Checkout/hooks/useCheckoutSettings';
import {
  PATHS,
  IS_SDK_MODE,
  addPersonalDataFormRoute,
  verificationTypesRoutes,
} from 'routes/constants';
import AddPersonalDataForm from 'pages/AddPersonalDataForm';
import GuestRegistrationView from 'pages/GuestRegistrationView';
import IdentityVerification from 'pages/IdentityVerification';
import GuidebookView from 'pages/GuidebookView';
import FAQView from './pages/FAQView';
import DepositView from './pages/DepositView';
import HomeView from 'pages/HomeView';
import RemoteAccessView from 'pages/RemoteAccess';
import RootLayout from './components/RootLayout';
import SearchReservation from 'pages/SearchReservation';
import ConnectionErrorsHandler from 'components/ConnectionErrorsHandler';
import Payments, {PaymentFormView, PaymentsView} from './pages/Payments';
import {
  CheckoutView,
  BeforeYouLeaveView,
  RateYourStayView,
  ThanksView,
} from './pages/Checkout';
import MRZView, {MRZScanView, MRZStartView} from './pages/MRZView';
import {OnlyGuestFormRoutes} from 'routes/OnlyGuestFormRoutes';
import {OnlyGuestRegistrationRoutes} from 'routes/OnlyGuestRegistrationRoutes';
import {TaxesExemptionView, TaxesTouristView} from './pages/Taxes';
import {OfferView} from 'pages/OfferView';
import {BookExperienceView} from 'pages/BookExperienceView';
import {EnterContactEmailView} from 'pages/EnterContactEmailView';
import {OfferListView} from 'pages/OfferListView';
import {WhoIsRegistering} from 'pages/WhoIsRegistering';
import {OnlyIVRoutes} from 'routes/OnlyIVRoutes';
import {OnlyPropertyLinkRoutes} from 'routes/OnlyPropertyLinkRoutes';
import {OAuthCallback} from 'pages/Auth/views/OAuthCallback';
import {RemoteKeys} from 'components/RemoteAccess/RemoteKeys';
import {
  BackScanView,
  DocumentSelectionView,
  FrontScanView,
  SelfieScanView,
  StartView,
  UploadingTypeView,
  ScanQRCodeView,
  SelfieLivenessView,
  SelfieInstructionsView,
  ContactsVerificationView,
} from 'pages/IdentityVerification/views';
import {
  BookingCodeView,
  CheckInDateView,
  NewReservationView,
} from 'pages/SearchReservation/Views';
import {
  LoginView,
  WelcomeView,
  Onboarding,
  NumberOfGuestsStep,
  ReservationTypeStep,
  WhoIsComingStep,
  ConfirmGuestLeaderStep,
} from 'pages/Auth';
import {ManageEsimView} from 'pages/esim/ManageEsimView';
import {ChoosePlanView} from 'pages/esim/ChoosePlanView';
import {MyEsimsView} from 'pages/esim/MyEsimsView';

function AppRouter() {
  useUniversalLink();
  useInitGuestTaxes();
  useInitPropertyProtection();
  useSetPreviousPage();
  const {isTemplateInitialized} = useCustomTheme();
  const {getFullPath} = usePath();
  const {housing} = useReservation();
  const {isInstantCheckInEnabled, isLoading} = useSummary();
  const {isCheckoutEnabled} = useCheckoutSettings();

  const isLoadingMinimalData = !isTemplateInitialized || isLoading;

  const routes = useMemo(() => {
    if (IS_SDK_MODE && window.ChekinProSettings?.mode) {
      const mode = window.ChekinProSettings.mode;

      if (!isTemplateInitialized) {
        return null;
      }

      if (mode === SDK_MODE.onlyGuestForm) {
        return <Route path="*" index element={<OnlyGuestFormRoutes />} />;
      }

      if (mode === SDK_MODE.onlyIV) {
        return <Route path="*" index element={<OnlyIVRoutes />} />;
      }

      if (mode === SDK_MODE.propertyLink) {
        return <Route path="*" index element={<OnlyPropertyLinkRoutes />} />;
      }

      if (mode === SDK_MODE.guestRegistrationOnly) {
        return <Route path="*" index element={<OnlyGuestRegistrationRoutes />} />;
      }
    }

    return (
      <Route element={<RootLayout isLoading={isLoadingMinimalData} />}>
        <Route index element={<HomeView />} />
        <Route path={PATHS.guestRegistration} element={<GuestRegistrationView />} />

        {isInstantCheckInEnabled && (
          <>
            <Route path={PATHS.whoIsRegistering} element={<WhoIsRegistering />} />
            <Route path={PATHS.auth.root}>
              <Route index element={<LoginView />} />
              <Route path={PATHS.auth.createAccount} element={<LoginView />} />
              <Route path={PATHS.auth.login} element={<LoginView />} />
              <Route path={PATHS.auth.welcome} element={<WelcomeView />} />
            </Route>
            <Route path={PATHS.onboarding.root} element={<Onboarding />}>
              <Route index element={<Navigate to="number-of-guest" replace />} />
              <Route
                path={PATHS.onboarding.reservationType}
                element={<ReservationTypeStep disabled={false} />}
              />
              <Route
                path={PATHS.onboarding.numberOfGuests}
                element={<NumberOfGuestsStep />}
              />
              <Route path={PATHS.onboarding.whoIsComing} element={<WhoIsComingStep />} />
              <Route
                path={PATHS.onboarding.confirmLeaderGuest}
                element={<ConfirmGuestLeaderStep />}
              />
            </Route>
          </>
        )}
        {addPersonalDataFormRoute.map((route, index) => {
          return <Route key={index} path={route} element={<AddPersonalDataForm />} />;
        })}
        <Route path={PATHS.deposits} element={<DepositView />}>
          <Route path=":id" element={<DepositView />} />
        </Route>
        <Route path={PATHS.payments.main} element={<Payments />}>
          <Route index path="*" element={<PaymentsView />} />
          <Route path={PATHS.payments.form} element={<PaymentFormView />} />
        </Route>
        {isCheckoutEnabled && (
          <Route path={PATHS.checkout.main} element={<CheckoutView />}>
            <Route index element={<BeforeYouLeaveView />} />
            <Route
              path={PATHS.checkout.beforeYouLeave}
              element={<BeforeYouLeaveView />}
            />
            <Route path={PATHS.checkout.rateYourStay} element={<RateYourStayView />} />
            <Route path={PATHS.checkout.thanks} element={<ThanksView />} />
          </Route>
        )}
        <Route path={PATHS.taxes.main}>
          <Route index element={<TaxesTouristView />} />
          <Route path={PATHS.taxes.exemptions} element={<TaxesExemptionView />}>
            <Route path=":id" element={<TaxesExemptionView />} />
          </Route>
        </Route>

        {verificationTypesRoutes.map((route, index) => {
          return (
            <Route key={index} path={route} element={<IdentityVerification />}>
              <Route index element={<StartView />} />
              <Route path={PATHS.verification.scanQRCode} element={<ScanQRCodeView />} />
              <Route
                path={PATHS.verification.contacts}
                element={<ContactsVerificationView />}
              />
              <Route
                path={PATHS.verification.documentSelection}
                element={<DocumentSelectionView />}
              />
              <Route
                path={PATHS.verification.uploadingType}
                element={<UploadingTypeView />}
              >
                <Route path={':type'} element={<UploadingTypeView />} />
              </Route>
              <Route path={PATHS.verification.front} element={<FrontScanView />}>
                <Route path={':uploadType'} element={<FrontScanView />} />
              </Route>
              <Route path={PATHS.verification.back} element={<BackScanView />}>
                <Route path={':uploadType'} element={<BackScanView />} />
              </Route>
              <Route
                path={PATHS.verification.selfieInstructions}
                element={<SelfieInstructionsView />}
              />
              <Route
                path={PATHS.verification.selfie}
                element={
                  housing?.is_liveness_detection_enabled ? (
                    <SelfieLivenessView />
                  ) : (
                    <SelfieScanView />
                  )
                }
              />
              {/* <Route
                path="*"
                element={<Navigate to={`./../${PATHS.verification.main}`} replace />}
              /> */}
            </Route>
          );
        })}
        <Route path={PATHS.documentMRZ} element={<MRZView />}>
          <Route index element={<MRZStartView />} />
          <Route path={':uploadType'} element={<MRZScanView />} />
        </Route>
        <Route path={PATHS.remoteAccess} element={<RemoteAccessView />} />
        <Route path={PATHS.remoteKeys} element={<RemoteKeys />} />
        <Route path={PATHS.guidebook} element={<GuidebookView />}>
          <Route path=":id" element={<GuidebookView />} />
        </Route>
        <Route path={PATHS.faq} element={<FAQView />} />
        <Route path={PATHS.searchReservation.main}>
          <Route index element={<SearchReservation />} />
        </Route>
        <Route path={PATHS.searchReservation.bookingCode} element={<BookingCodeView />} />
        <Route
          path={PATHS.searchReservation.checkingDate}
          element={<CheckInDateView />}
        />
        <Route
          path={PATHS.searchReservation.newReservation}
          element={<NewReservationView />}
        />
        <Route path={PATHS.recommendations.main}>
          <Route index element={<OfferListView />} />
          <Route path={PATHS.recommendations.details.main}>
            <Route
              path={PATHS.recommendations.details.bookExperience}
              element={<BookExperienceView />}
            />
            <Route
              path={PATHS.recommendations.details.confirmEmail}
              element={<EnterContactEmailView />}
            />
            <Route index element={<OfferView />} />
          </Route>
        </Route>
        <Route path={PATHS.esim.manage} element={<ManageEsimView />} />
        <Route path={PATHS.esim.choosePlan} element={<ChoosePlanView />} />
        <Route path={PATHS.esim.my} element={<MyEsimsView />} />
        <Route path="*" element={<Navigate to={getFullPath(PATHS.home)} />} />
      </Route>
    );
  }, [
    getFullPath,
    isTemplateInitialized,
    housing?.is_liveness_detection_enabled,
    isInstantCheckInEnabled,
    isLoadingMinimalData,
    isCheckoutEnabled,
  ]);

  return (
    <Routes>
      <Route path={PATHS.oauth.google} element={<OAuthCallback />} />
      <Route path="/" element={<ConnectionErrorsHandler />}>
        {IS_SDK_MODE ? (
          <Route path="/*">{routes}</Route>
        ) : (
          <Route path={PATHS.home}>{routes}</Route>
        )}
      </Route>
    </Routes>
  );
}
export default AppRouter;
export {PATHS};
